import { FC, useMemo, useRef, useState } from "react";
import styles from "./LaserScanAnalysis.module.less";
import TypeComponentCard from "../typeComponentCard/TypeComponentCard";
import { ThermalLiDARResponseParameters } from "~/pages/copilotMessage/types/copilot";
import { Spin, Tooltip, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import VisualMapColorScaler from "./components/lidarColorScale/VisualMapColorScaler";
import { MouseAndKeyboardTutorial2D } from "~/components/2DTutorial/MouseAndKeyboardTutorial2D";

import { convertInchToUserPreference, getUnit } from "~/helpers/userPreference";
import FormattedMessage from "~/components/formattedMessage/FormattedMessage";
import {
  VISUAL_MAP_BOTTOM_COLORS_SCALE,
  VISUAL_MAP_SIDEWALL_COLORS_SCALE,
} from "../../constants/heatMapValues";
import { UNITS } from "~/constants/unit";
import { useLiDARHeatData } from "./hooks/useLiDARHeatData";
import { getOrdinalSuffix } from "~/helpers/number";
import { useGetProcessedThermalImages } from "../irScanAnalysis/hooks/useGetProcessedThermalImages";
import { useGetLiDARDataVisualizationSummariesForLastSpecificHeats } from "./hooks/useGetLiDARDataVisualizationSummariesForLastSpecificHeats";
import LidarSidewallWrapper from "./components/lidar/components/lidarSidewall/LidarSidewallWrapper";
import LidarBottomWrapper from "./components/lidar/components/lidarBottom/LidarBottomWrapper";
import LaserScanSceneProvider from "../laserScanScene/providers/LaserScanSceneProvider";
import LaserScanScene from "../laserScanScene/LaserScanScene";

interface LaserScanAnalysisProps {
  parameters: ThermalLiDARResponseParameters;
}

const LaserScanAnalysis: FC<LaserScanAnalysisProps> = ({ parameters }) => {
  const [sidewallFullScreen, setSidewallFullScreen] = useState(false);
  const [bottomFullScreen, setBottomFullScreen] = useState(false);
  const [sceneFullScreen, setSceneFullScreen] = useState(false);

  const currentHeatInfoRef = useRef<HTMLSpanElement | null>(null);

  const lidarParams = useMemo(() => {
    return parameters.LiDARData;
  }, [parameters]);

  const {
    bottomVisualizationSummaries,
    sidewallVisulizationSummaries,
    lastAggregatedData,
    visualizationSummariesLoading,
  } = useGetLiDARDataVisualizationSummariesForLastSpecificHeats(
    lidarParams.CampaignHeatId,
  );

  const splittedFileName = useMemo(() => {
    const fileName = lidarParams.LiDARMeasurementsCloudFileKey;

    if (!fileName) return "";
    else {
      const splittedFileName = fileName.split("/");
      return splittedFileName[splittedFileName.length - 1]
        .replace("_Vertex_Data", "")
        .split(".")
        .slice(0, -1)
        .join(".");
    }
  }, [lidarParams]);

  const { heatTexts, defaultIndex } = useLiDARHeatData(
    sidewallVisulizationSummaries,
    lidarParams,
    {
      textMapper: (heatNumber, counter) =>
        counter[heatNumber]
          ? `${counter[heatNumber]}${getOrdinalSuffix(
              counter[heatNumber],
            )} laser, `
          : "",
    },
  );

  const CampaignInfoSubTitle = useMemo(() => {
    return (
      <div style={{ display: "flex", gap: "10px" }}>
        <span>{`Campaign: ${lidarParams?.CampaignName}`}</span>
        <span ref={currentHeatInfoRef} />
      </div>
    );
  }, [lidarParams]);

  const thermalParams = useMemo(() => {
    return parameters.ProcessedHeatThermalData;
  }, [parameters]);

  const { processedThermalImages } = useGetProcessedThermalImages(
    thermalParams?.CampaignHeatId,
    thermalParams?.SetId,
  );

  return (
    <div className={styles.LaserScanAnalysis}>
      <div className={styles.chartContainer}>
        <TypeComponentCard
          title={"3D Map"}
          onShowFullScreen={setSceneFullScreen}
          extraComponent={
            <Typography.Title level={3}>
              {heatTexts[defaultIndex]}
              {splittedFileName}
            </Typography.Title>
          }
        >
          {visualizationSummariesLoading ? (
            <Spin size="large" />
          ) : (
            <>
              <LaserScanSceneProvider>
                <LaserScanScene
                  isExpanded={sceneFullScreen}
                  lidarParams={lidarParams}
                  lidarZoneSectionData={lastAggregatedData}
                  processedThermalImages={processedThermalImages}
                />
              </LaserScanSceneProvider>
              {sceneFullScreen && (
                <Tooltip
                  overlay={<MouseAndKeyboardTutorial2D />}
                  placement="topRight"
                  fresh
                  overlayInnerStyle={{ width: "300px" }}
                  getPopupContainer={(trigger) =>
                    trigger.parentElement || document.body
                  }
                >
                  <InfoCircleOutlined className={styles.infoButton} />
                </Tooltip>
              )}
            </>
          )}
        </TypeComponentCard>

        <TypeComponentCard
          title={"Progress of Refractory Wear Sidewall (Video)"}
          subTitle={sidewallFullScreen ? CampaignInfoSubTitle : undefined}
          onShowFullScreen={setSidewallFullScreen}
        >
          {visualizationSummariesLoading ||
          sidewallVisulizationSummaries.length === 0 ? (
            <Spin size="large" />
          ) : (
            <LidarSidewallWrapper
              data={sidewallVisulizationSummaries}
              isExpanded={sidewallFullScreen}
              lidarParams={lidarParams}
              currentHeatInfoRef={currentHeatInfoRef}
            />
          )}
        </TypeComponentCard>

        <TypeComponentCard
          title={"Progress of Refractory Wear Bottom (Video)"}
          subTitle={bottomFullScreen ? CampaignInfoSubTitle : undefined}
          onShowFullScreen={setBottomFullScreen}
        >
          {visualizationSummariesLoading ||
          bottomVisualizationSummaries.length === 0 ? (
            <Spin size="large" />
          ) : (
            <LidarBottomWrapper
              data={bottomVisualizationSummaries}
              isExpanded={bottomFullScreen}
              lidarParams={lidarParams}
              currentHeatInfoRef={currentHeatInfoRef}
            />
          )}
        </TypeComponentCard>
      </div>
      <div className={styles.scaleContainer}>
        <div className={styles.scales}>
          <div className={styles.colorScaleContainer}>
            <span>
              <FormattedMessage id="LaserScanAnalysis.Label.Sidewall" />
              {` (${getUnit(UNITS.MILLIMETER)})`}
            </span>
            <VisualMapColorScaler
              converter={convertInchToUserPreference}
              colorScale={VISUAL_MAP_SIDEWALL_COLORS_SCALE}
            />
          </div>

          <div className={styles.colorScaleContainer}>
            <span>
              <FormattedMessage id="LaserScanAnalysis.Label.Bottom" />
              {` (${getUnit(UNITS.MILLIMETER)})`}
            </span>
            <VisualMapColorScaler
              converter={convertInchToUserPreference}
              colorScale={VISUAL_MAP_BOTTOM_COLORS_SCALE}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LaserScanAnalysis;
